import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  FormControlLabel,
  Switch,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { ArtistFeedback } from '../../types/PostProduction';

interface ArtistFeedbackSectionProps {
  artistFeedback: ArtistFeedback;
  editMode: boolean;
  onChange: (field: string, value: any) => void;
}

const ArtistFeedbackSection: React.FC<ArtistFeedbackSectionProps> = ({
  artistFeedback,
  editMode,
  onChange,
}) => {
  // Artist feedback categories from proposal
  const artistFeedbackCategories = [
    'Equipment requests',
    'Venue praise',
    'Venue concerns',
    'Hospitality feedback',
    'Technical feedback',
    'Booking interest',
    'Other'
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Artist Feedback
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Artist Satisfaction</Typography>
            <Rating
              name="artist-satisfaction"
              value={artistFeedback.artistSatisfaction}
              onChange={(e, value) => onChange('artistSatisfaction', value || 0)}
              disabled={!editMode}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Their Venue Rating</Typography>
            <Rating
              name="venue-rating"
              value={artistFeedback.venueRating}
              onChange={(e, value) => onChange('venueRating', value || 0)}
              disabled={!editMode}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Their Hospitality Rating</Typography>
            <Rating
              name="hospitality-rating"
              value={artistFeedback.hospitalityRating}
              onChange={(e, value) => onChange('hospitalityRating', value || 0)}
              disabled={!editMode}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={artistFeedback.returnInterest}
                onChange={(e) => onChange('returnInterest', e.target.checked)}
                disabled={!editMode}
              />
            }
            label="Expressed Interest in Returning"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {/* NEW: Artist Feedback Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Artist Feedback Categories</InputLabel>
            <Select
              multiple
              value={artistFeedback.artistFeedbackCategories || []}
              onChange={(e) => onChange('artistFeedbackCategories', e.target.value)}
              input={<OutlinedInput label="Artist Feedback Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {artistFeedbackCategories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Artist Comments Free Text (Backwards Compatible) */}
          <TextField
            label="Artist Comments"
            multiline
            rows={4}
            fullWidth
            value={artistFeedback.artistComments}
            onChange={(e) => onChange('artistComments', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for full comments"
          />

          {/* EXISTING: Staff Notes (Backwards Compatible) */}
          <TextField
            label="Staff Notes"
            multiline
            rows={4}
            fullWidth
            value={artistFeedback.staffNotes}
            onChange={(e) => onChange('staffNotes', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Internal notes about the artist"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ArtistFeedbackSection;